import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Dialog } from "../../../../../../../utils/Dialog/Dialog.component";
import FormSet from "../../../../../../../utils/forms/components/FormSet";
import { ConfigurationsActionCreators } from "../../../../../actions/actionCreators";
import {
    ProgressiveConfig,
    SpecialPrizeConfiguration,
    TerminalGroupConfiguration
} from "../../../../../reducers/editingConfiguration.reducer";
import { configurationsStoreSelectors } from "../../../../../store/store";
import terminalSubform from "./FormBuild/TerminalGroups.form";

import "./TerminalGroups.component.scss";

import { checkProgressivesHaveGroups } from "../../../../Utils/parse-available-groups";

const useStateSlices = () => {
    const {
        groupsSchema,
        groupsConfig,
        progressivesConfig,
        specialPrizesConfig
    } = configurationsStoreSelectors;

    const schema = useSelector(groupsSchema);
    const groupsConfigSlice = useSelector(groupsConfig);
    const specialPrizesConfigs = useSelector(specialPrizesConfig);
    const progressivesConfigs = useSelector(progressivesConfig);

    return {
        schema,
        groupsConfig: groupsConfigSlice,
        specialPrizesConfigs,
        progressivesConfigs
    };
};

const useDispatchGroupConfig = () => {
    const dispatch = useDispatch();

    const progressivesConfigs = useSelector(configurationsStoreSelectors.progressivesConfig);
    const currentFormState = useSelector(configurationsStoreSelectors.configFormValid);


    return formEvent => {
        const { values: config, isValid } = formEvent;

        dispatch(
            ConfigurationsActionCreators.updateGroupsConfig({
                config,
                isValid,
                type: "Terminal_Group"
            })
        );

        dispatch(
            ConfigurationsActionCreators.clearGroupTypes("terminal")
        );

        dispatch(
            ConfigurationsActionCreators.updateProgressivesConfig({
                config: progressivesConfigs,
                isValid: currentFormState['progressives']
                    ? checkProgressivesHaveGroups(progressivesConfigs,"terminal_groups")
                    : currentFormState['progressives'],
                groupType: "terminal"
            })
        );
    };
};

const useCheckValidityEffect = () => {
    const currentGroupConfig = useSelector(
        configurationsStoreSelectors.groupsConfig
    );
    const currentFormState = useSelector(
        configurationsStoreSelectors.configFormValid
    );
    const dispatch = useDispatch();

    useEffect(() => {
        if (currentGroupConfig.length === 0 && currentFormState["groups"]) {
            dispatch(
                ConfigurationsActionCreators.updateGroupsConfig({
                    config: [],
                    isValid: false
                })
            );
        }
    }, [currentGroupConfig]);
};

const useDeleteGroupDialog = (
    groupsConfig: TerminalGroupConfiguration[],
    progressivesConfigs: ProgressiveConfig[],
    specialPrizesConfigs: SpecialPrizeConfiguration[]
) => {
    const defaultState = {
        showDialog: false,
        indexToDelete: null,
        deleteCallback: null
    };
    const [deleteState, setDeleteState] = useState(defaultState);

    const _checkIfGroupInProgressives = (index: number) => {
        const group = groupsConfig[index];
        let isPresent = false;

        isPresent = progressivesConfigs.some(item => {
            if (
                item.type == "levels" ||
                item.type == "easylink" ||
                item.type == "sap" ||
                item.type == "multipots" ||
                item.type == "jumbolink" ||
                item.type == "reelstrike" ||
                item.type == "luckytokenslink"
            ) {
                if (
                    item.terminal_groups &&
                    item.terminal_groups.includes(group.id)
                ) {
                    return true;
                } else {
                    let tempConst = false;
                    item["children"].map(child => {
                        if (child.terminal_groups.includes(group.id))
                            tempConst = true;
                    });
                    return tempConst;
                }
            } else {
                return item.terminal_groups.includes(group.id);
            }
        });

        if (!isPresent) {
            isPresent = specialPrizesConfigs.some(item =>
                item.terminal_groups.includes(group.id)
            );
        }

        return isPresent;
    };

    const onDeleteGroup = (deleteCallback, index) => {
        if (_checkIfGroupInProgressives(index)) {
            setDeleteState({
                showDialog: true,
                indexToDelete: index,
                deleteCallback
            });
        } else {
            deleteCallback(index);
            setDeleteState(defaultState);
        }
    };

    const onDialogConfirm = () => {
        deleteState.deleteCallback(deleteState.indexToDelete);
        setDeleteState(defaultState);
    };

    const onDialogClose = () => setDeleteState(defaultState);

    return {
        showDialog: deleteState.showDialog,
        onDeleteGroup,
        onDialogConfirm,
        onDialogClose
    };
};

const TerminalGroups = () => {
    const { schema, groupsConfig, progressivesConfigs, specialPrizesConfigs } =
        useStateSlices();
    const { showDialog, onDeleteGroup, onDialogConfirm, onDialogClose } =
        useDeleteGroupDialog(
            groupsConfig,
            progressivesConfigs,
            specialPrizesConfigs
        );
    const onChangeHandler = useDispatchGroupConfig();
    useCheckValidityEffect();

    const terminalFormSet = useRef();

    const addNewTerminal = ref => {
        ref.current.onAdd(0);
    };

    return (
        <div id="terminal-groups-container">
            <div className="inner-card">
                <div className="inner-card-title">
                    <p>
                        Groups
                        <span>{` (${groupsConfig.length})`}</span>
                    </p>
                    <button onClick={() => addNewTerminal(terminalFormSet)}>
                        <i className="icon-plus-light"></i>
                    </button>
                </div>
                <div className="inner-card-content">
                    <FormSet
                        ref={terminalFormSet}
                        subFormTypes={terminalSubform({ schema, groupsConfig })}
                        initialValues={groupsConfig}
                        onChange={onChangeHandler}
                        onDelete={onDeleteGroup}
                        hideAdd
                        isConfig={true}
                    />

                    <Dialog
                        open={showDialog}
                        onConfirm={onDialogConfirm}
                        onClose={onDialogClose}
                    >
                        You are about to delete a terminal group that may be
                        included in a Progressive/Special Prize configuration.
                        Are you sure?
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default TerminalGroups;
